import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Copy, Flame, Trophy, Gift } from "lucide-react";
import { ReferralLeaderboard } from "@/components/Social";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtOdds, money, pct, SPORT_META } from "@/lib/odds";
import { TierBadge } from "@/components/TierBadge";
import { Badges } from "@/components/Badges";
import { useAuth } from "@/context/AuthContext";
import { useSlip } from "@/context/SlipContext";
import { FollowBettorButton } from "@/components/FollowBettorButton";
import { CrewBoard } from "@/components/CrewBoard";
import { Users } from "lucide-react";

const FRAMES = [["week", "This week"], ["month", "This month"], ["all", "All time"]];
const MEDAL = ["text-amber-300", "text-slate-300", "text-amber-600"];

function CopyButton({ row }) {
  const { user } = useAuth();
  const { replace } = useSlip();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const copy = async (e) => {
    e.stopPropagation();
    if (!user) return navigate("/login", { state: { from: "/leaderboard" } });
    setBusy(true);
    try {
      const { data } = await api.post(`/parlays/${row.parlay_id}/copy`);
      replace(data.legs, { title: data.title, strategy: data.strategy });
      toast.success(`Ticket copied to your slip — ${data.note}`);
      navigate("/builder");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button data-testid="leaderboard-copy-parlay-btn" disabled={busy} onClick={copy} title={row.copyable ? "Copy this ticket into your slip" : "Rebuild this ticket with today's closest equivalent lines"} className="btn-ghost !px-3 !py-1.5 text-xs">
      <Copy size={13} /> {busy ? "…" : row.copyable ? "Copy" : "Copy for today"}
    </button>
  );
}

function Row({ r, i, open, setOpen, showProfit = true }) {
  return (
    <div data-testid={showProfit ? "leaderboard-row" : "trending-row"} className={`glass-card fade-up overflow-hidden ${i === 0 && showProfit ? "!border-amber-400/50" : ""}`} style={{ animationDelay: `${Math.min(i, 10) * 40}ms` }}>
      <div role="button" tabIndex={0} onClick={() => setOpen(open === r.parlay_id ? null : r.parlay_id)} className="flex w-full cursor-pointer items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-[#182030]">
        <span className={`font-display w-10 text-3xl font-black ${MEDAL[i] || "text-slate-500"}`}>{i < 3 && showProfit ? <Trophy size={22} className="inline" /> : `#${r.rank}`}</span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2"><span className="truncate font-semibold text-white" data-testid="leaderboard-user">{r.user_name}</span><TierBadge tier={r.user_tier} /><Badges badges={r.badges} /><FollowBettorButton userId={r.user_id} name={r.user_name} /></div>
          <p className="truncate text-xs text-slate-500">{r.title} · {r.legs.length} legs · {new Date(r.created_at).toLocaleDateString()}</p>
        </div>
        <div className="hidden text-right sm:block"><p className="text-[10px] uppercase tracking-wider text-slate-500">Odds</p><p className="font-mono-num font-bold text-amber-400" data-testid="leaderboard-odds">{fmtOdds(r.combined_american)}</p></div>
        <div className="text-right"><p className="text-[10px] uppercase tracking-wider text-slate-500">Multiplier</p><p className="font-mono-num font-bold text-emerald-400">{r.combined_decimal.toFixed(2)}x</p></div>
        {showProfit ? (
          <div className="hidden text-right md:block"><p className="text-[10px] uppercase tracking-wider text-slate-500">Profit</p><p className="font-mono-num font-bold text-white">+{money(r.profit)}</p></div>
        ) : (
          <div className="hidden text-right md:block"><p className="text-[10px] uppercase tracking-wider text-slate-500">Hit prob</p><p className="font-mono-num font-bold text-white">{pct(r.win_probability)}</p></div>
        )}
        <CopyButton row={r} />
        <ChevronDown size={16} className={`text-slate-500 transition-transform ${open === r.parlay_id ? "rotate-180" : ""}`} />
      </div>
      {open === r.parlay_id && (
        <ul className="divide-y divide-[#26334D]/60 border-t border-[#26334D] bg-[#0B0E14]/50" data-testid="leaderboard-legs">
          {r.legs.map((l) => (
            <li key={l.pick_id} className="flex items-center gap-3 px-5 py-2 text-sm">
              <span className="h-5 w-1 rounded-full" style={{ background: SPORT_META[l.sport]?.color }} />
              <span className="min-w-0 flex-1 truncate"><span className="text-slate-500">{l.matchup} · </span><span className="font-semibold text-white">{l.selection}</span></span>
              <span className="font-mono-num text-amber-400">{fmtOdds(l.odds)}</span>
              <span className={`text-xs font-bold uppercase ${l.result === "won" ? "text-emerald-400" : l.result === "lost" ? "text-red-400" : "text-slate-500"}`}>{l.result === "pending" ? "live" : l.result}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function Leaderboard() {
  const [frame, setFrame] = useState("week");
  const [rows, setRows] = useState(null);
  const [trending, setTrending] = useState([]);
  const [open, setOpen] = useState(null);
  const [tab, setTab] = useState("community");
  const { user } = useAuth();
  useEffect(() => { setRows(null); api.get(`/leaderboard?timeframe=${frame}`).then((r) => setRows(r.data)).catch(() => setRows([])); }, [frame]);
  useEffect(() => { api.get("/leaderboard/trending").then((r) => setTrending(r.data)).catch(() => {}); }, []);

  return (
    <main className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8" data-testid="leaderboard-container">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="label-caps text-emerald-400">Community</p>
          <h1 className="font-display mt-2 text-4xl font-black uppercase tracking-tight text-white sm:text-5xl">Top Winning Parlays</h1>
        </div>
        {tab === "community" && (
          <div className="flex gap-2" data-testid="leaderboard-timeframe-filter">
            {FRAMES.map(([k, l]) => <button key={k} data-testid={`leaderboard-frame-${k}`} onClick={() => setFrame(k)} className={`rounded-full border px-4 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${frame === k ? "border-emerald-500 bg-emerald-500/15 text-emerald-300" : "border-[#26334D] text-slate-400 hover:text-white"}`}>{l}</button>)}
          </div>
        )}
      </div>
      {user && (
        <div className="mt-6 inline-flex rounded-full border border-[#26334D] bg-[#121824] p-1" data-testid="leaderboard-tabs">
          {[["community", "Community", Trophy], ["crew", `My Crew${user.following?.length ? ` · ${user.following.length}` : ""}`, Users], ["referrals", "Top Inviters", Gift]].map(([k, l, Icon]) => (
            <button key={k} data-testid={`leaderboard-tab-${k}`} onClick={() => setTab(k)} className={`flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${tab === k ? "bg-emerald-500 text-[#0B0E14]" : "text-slate-400 hover:text-white"}`}><Icon size={15} /> {l}</button>
          ))}
        </div>
      )}
      {tab === "crew" && user && <CrewBoard RowComponent={Row} open={open} setOpen={setOpen} />}
      {tab === "referrals" && user && <ReferralLeaderboard />}
      {tab === "community" && (<>

      {trending.length > 0 && (
        <section className="mt-8" data-testid="trending-section">
          <h2 className="font-display flex items-center gap-2 text-2xl font-bold uppercase tracking-tight text-white"><Flame size={20} className="text-orange-400" /> Trending today <span className="text-sm font-normal normal-case tracking-normal text-slate-500">— live community tickets you can copy</span></h2>
          <div className="mt-4 space-y-3">{trending.map((r, i) => <Row key={r.parlay_id} r={r} i={i} open={open} setOpen={setOpen} showProfit={false} />)}</div>
        </section>
      )}

      <h2 className="font-display mt-10 text-2xl font-bold uppercase tracking-tight text-white">Biggest cashed tickets</h2>
      <div className="mt-4 space-y-3">
        {!rows && [0, 1, 2].map((i) => <div key={i} className="glass-card h-16 animate-pulse" />)}
        {rows && rows.length === 0 && <p className="glass-card p-10 text-center text-sm text-slate-500" data-testid="leaderboard-empty">No winning parlays in this window yet.</p>}
        {rows?.map((r, i) => <Row key={r.parlay_id} r={r} i={i} open={open} setOpen={setOpen} />)}
      </div>
      </>)}
    </main>
  );
}
